// Step1Verify — sub-step orchestration for Step 1 (verifications).
//
// Spec: docs/specs/location-tasks.md §8.4.
//
// PR J introduces the framework: enumerate which sub-steps are enabled +
// render their completion state. Each sub-step's *flow* (challenge fetch,
// SDK call, evidence POST) lands in PR K via the slot props below — so
// this file already calls the production endpoints when slots are wired.
//
// Until PR K wires the slots, an unfilled enabled sub-step renders a
// disabled "尚未支援" CTA. This is intentional: with no inline TODOs, the
// surface cleanly shows what's missing, and PR K becomes a pure addition
// (drop in the renderer + the slot, no re-architecting).

import React from "react";
import { Card, CardHeader } from "../../../liff/components/Mobile.js";
import { Fingerprint, ScanFace, MapPin, CheckCircle2 } from "lucide-react";
import type { WizardData } from "./types.js";
import { formatTaipeiTime } from "../../../lib/time.js";

interface Props {
  data: WizardData;
  /** PR K replaces these with real renderers. Each must call onComplete()
   *  after writing the verify_* fields server-side; framework re-fetches
   *  the submission and advances when all enabled sub-steps are complete. */
  renderDevice?: (ctx: { data: WizardData; onComplete: () => void }) => React.ReactElement;
  renderFace?:   (ctx: { data: WizardData; onComplete: () => void }) => React.ReactElement;
  renderGeo?:    (ctx: { data: WizardData; onComplete: () => void }) => React.ReactElement;
  onChanged: () => void;
}

export default function Step1Verify({ data, renderDevice, renderFace, renderGeo, onChanged }: Props): React.ReactElement {
  const t = data.task;
  const s = data.submission;
  const blocks: React.ReactNode[] = [];

  if (t.verify_device === 1) {
    const done = !!s.verify_device_assertion_at;
    blocks.push(
      <Card key="dev">
        <CardHeader title="裝置驗證" hint={done ? "已完成" : "WebAuthn"} />
        <SubStepHeader icon={<Fingerprint className="h-4 w-4" />} done={done}
          label="使用此裝置已綁定的 passkey 簽署一次 challenge。" />
        {done ? (
          <DoneNote ts={s.verify_device_assertion_at!} />
        ) : renderDevice ? renderDevice({ data, onComplete: onChanged }) : <NotImplementedCta />}
      </Card>
    );
  }
  if (t.verify_face === 1) {
    const done = !!s.verify_face_liveness_at;
    blocks.push(
      <Card key="face">
        <CardHeader title="人臉驗證" hint={done ? "已完成" : "活體挑戰"} />
        <SubStepHeader icon={<ScanFace className="h-4 w-4" />} done={done}
          label="完成隨機動作並對比已註冊的 5 個樣本。" />
        {done ? (
          <DoneNote ts={s.verify_face_liveness_at!} extra={s.verify_face_match_score != null ? `比對分數 ${(s.verify_face_match_score * 100).toFixed(0)}/100` : undefined} />
        ) : renderFace ? renderFace({ data, onComplete: onChanged }) : <NotImplementedCta />}
      </Card>
    );
  }
  // Geo verify re-enabled in scan-wizard-v2 PR-D. Previously hidden on
  // the assumption「NTAG424 已綁實體位置 → geo 冗餘」; that's true for
  // attendance-only taps, but tasks with verify_geo=1 explicitly require
  // an independent GPS-derived proof of location (e.g.「巡邏員必須真正
  //走到大廳中央而非 tap 完就離開」). After PR-C the strict gate at
  // /scan/finalize blocks finalization until session-level geo is done,
  // so this card is mostly a「✓ 已完成 (inherited)」display in the LIFF
  // wizard. The renderGeo slot stays wired for the rare case where the
  // session somehow lost its geo result mid-flight.
  if (t.verify_geo === 1) {
    const done = !!s.verify_geo_at;
    blocks.push(
      <Card key="geo">
        <CardHeader title="位置驗證" hint={done ? "已完成" : "GPS"} />
        <SubStepHeader icon={<MapPin className="h-4 w-4" />} done={done}
          label="距離地點需小於設定的 geofence 半徑。" />
        {done ? (
          <DoneNote ts={s.verify_geo_at!} extra={s.verify_geo_distance_m != null ? `距離 ${s.verify_geo_distance_m.toFixed(1)} m` : undefined} />
        ) : renderGeo ? renderGeo({ data, onComplete: onChanged }) : <NotImplementedCta />}
      </Card>
    );
  }
  if (blocks.length === 0) {
    return (
      <Card>
        <p className="text-sm text-text-secondary">此任務未要求 Step 1 驗證。</p>
      </Card>
    );
  }
  return <>{blocks}</>;
}

function SubStepHeader({ icon, label, done }: { icon: React.ReactNode; label: string; done: boolean }): React.ReactElement {
  return (
    <div className={`flex items-start gap-2 mb-3 ${done ? "opacity-70" : ""}`}>
      <span className={`mt-0.5 ${done ? "text-success" : "text-text-secondary"}`}>{icon}</span>
      <p className="text-xs text-text-secondary leading-relaxed">{label}</p>
    </div>
  );
}

function DoneNote({ ts, extra }: { ts: number; extra?: string }): React.ReactElement {
  return (
    <p className="text-xs text-success inline-flex items-center gap-1">
      <CheckCircle2 className="h-3.5 w-3.5" />
      於 {formatTaipeiTime(ts)} 完成{extra ? ` · ${extra}` : ""}
    </p>
  );
}

function NotImplementedCta(): React.ReactElement {
  return (
    <button disabled className="block w-full h-11 rounded-xl bg-bg-raised text-text-muted text-sm font-medium">
      此驗證流程尚未上線
    </button>
  );
}
